/**
 * What the platform is holding, per currency.
 *
 * FOUR DISJOINT COLUMNS, and the fourth is named as a total. From `ledger.py`,
 * `payable == pending + available + reserved`, so the agreed three-column sketch
 * (held / available / in transit) put a containing figure beside its own parts and invited
 * the eye to add all three. Same information, no double count.
 *
 * NEVER A CROSS-CURRENCY TOTAL ROW — the design document forbids summing across currencies,
 * and the platform genuinely holds other currencies beside the one it settles in. The caption says
 * so, so the absence reads as deliberate rather than as a missing feature.
 *
 * A failed read renders em dashes, never zeros. "We hold nothing for this practice" and "we
 * could not find out" are different facts, and on a treasury screen the difference is the
 * whole point.
 */
import React from "react";

import { useLanguage, useT } from "../../lib/LanguageContext";
import { formatMoneyLedger } from "../../lib/formatMoney";
import { ALARM_ROW, COLHEAD, Figure, MUTED, RULE, SectionLabel } from "./Ledger";
import { liabilityTotalsToBuckets } from "./shapes";

export default function TreasuryStrip({ resource }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const { data, error, fetchedAt, loading } = resource;
  const failed = Boolean(error && data == null);
  // NOT-KNOWN is its own state. With no data, no error and no successful read yet, the strip
  // used to fall through to "No practice balances yet." — a factual claim about the
  // platform's money, made while it had not looked. This module's own docstring forbids
  // exactly that ("'we hold nothing' and 'we could not find out' are different facts"), and
  // it was the permanent state of the headline screen for as long as the liveness latch bug
  // lasted. `fetchedAt` is set only on a successful read, so it is the honest test.
  const notKnown = !failed && fetchedAt == null;
  const rows = (failed || notKnown) ? [] : liabilityTotalsToBuckets(data?.totals, {
    // The server states the runtime setting (`reconcile.liability_report`); a constant
    // here would order by a currency the operator may since have changed.
    settlementFirst: data?.settlementCurrency || "",
  });

  return (
    <section className="mb-6" data-testid="treasury-strip">
      <SectionLabel>{t("gw.console.treasury.title")}</SectionLabel>

      {failed && (
        <p className={`${ALARM_ROW} text-sm mt-2`} role="alert"
           data-testid="treasury-unreadable">
          {t("gw.console.treasury.unreadable")}
        </p>
      )}

      {notKnown && (
        <p className={`${MUTED} mt-2 italic`} data-testid="treasury-not-known"
           aria-live="polite">
          {loading ? t("gw.console.state.reading") : t("gw.console.state.neverRead")}
        </p>
      )}

      {!failed && !notKnown && rows.length === 0 && (
        <p className={`${MUTED} mt-2`} data-testid="treasury-empty">
          {t("gw.console.treasury.empty")}
        </p>
      )}

      {rows.length > 0 && (
        <>
          {/* Below 640px a four-figure row is unreadable, so each currency becomes its own
              stacked block instead of a squeezed grid. */}
          <div className="hidden sm:block mt-2">
            <table className="w-full text-sm tabular-nums">
              <thead>
                <tr className={RULE}>
                  <th className={`${COLHEAD} py-2`}>{t("gw.console.treasury.colCurrency")}</th>
                  <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.treasury.colOnHold")}</th>
                  <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.treasury.colReady")}</th>
                  <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.treasury.colInTransit")}</th>
                  <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.treasury.colOwed")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.currency} className={RULE} data-testid={`treasury-row-${r.currency}`}>
                    <td className="py-2 font-medium text-slate-700 uppercase">{r.currency}</td>
                    <td className="py-2"><Figure cents={r.onHoldCents} currency={r.currency} /></td>
                    <td className="py-2"><Figure cents={r.readyCents} currency={r.currency} /></td>
                    <td className="py-2"><Figure cents={r.inTransitCents} currency={r.currency} muted /></td>
                    <td className="py-2"><Figure cents={r.owedCents} currency={r.currency} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden mt-2 space-y-3">
            {rows.map((r) => (
              <div key={r.currency} className={`${RULE} pt-2`}
                   data-testid={`treasury-block-${r.currency}`}>
                <div className="font-medium text-slate-700 uppercase text-sm">{r.currency}</div>
                {[["colOnHold", r.onHoldCents], ["colReady", r.readyCents],
                  ["colInTransit", r.inTransitCents], ["colOwed", r.owedCents]].map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between">
                    <span className={MUTED}>{t(`gw.console.treasury.${k}`)}</span>
                    <Figure cents={v} currency={r.currency} />
                  </div>
                ))}
              </div>
            ))}
          </div>

          <p className={`${MUTED} mt-2`} data-testid="treasury-caption">
            {t("gw.console.treasury.noCrossCurrency")}
          </p>

          {rows.filter((r) => (r.debtCents || 0) > 0).map((r) => (
            <p key={r.currency} className={`${ALARM_ROW} text-sm mt-2`} role="alert"
               data-testid={`treasury-debt-${r.currency}`}>
              {t("gw.console.treasury.debt", {
                amount: formatMoneyLedger(r.debtCents, r.currency, lang),
              })}
            </p>
          ))}
        </>
      )}

    </section>
  );
}
